import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ChevronsDown,
  Camera,
  Images,
  Pin,
  Search,
  Users,
  UsersRound,
} from "lucide-react";
import {
  postTypingPing,
  useArchiveRoom,
  useChatMessagesAround,
  useChatMessagesInfinite,
  useChatRoom,
  useClearReaction,
  useDeleteMessage,
  useEditMessage,
  useMarkRead,
  useSendMessage,
  useSetRoomPinned,
  useToggleReaction,
  useUnarchiveRoom,
  useUpdateRoom,
} from "../api/chatApi";
import { useUploadFile } from "../api/files";
import { uploadErrorMessage } from "@/utils/managedFile";
import { setActiveChatRoom } from "../hooks/activeChatRoom";
import { useChatRoomLive } from "../hooks/useChatRoomLive";
import { usePageAttention } from "../hooks/usePageAttention";
import type { ChatMessage, ChatUserBrief } from "../types";
import { Avatar } from "./Avatar";
import { EditHistoryModal } from "./EditHistoryModal";
import { GroupMembersDialog } from "./GroupMembersDialog";
import { MessageComposer } from "./MessageComposer";
import {
  MessageList,
  type MessageListHandle,
} from "./MessageList";
import { ReactionsModal } from "./ReactionsModal";
import { RoomMediaPanel } from "./RoomMediaPanel";
import { SearchResultsPanel } from "./SearchResultsPanel";
import { TypingIndicator } from "./TypingIndicator";

interface ChatRoomViewProps {
  roomId: string;
  currentUserId: string | undefined;
  isDirector: boolean;
  isCeo: boolean;
  isArchivedView: boolean;
  showBack: boolean;
  onBack?: () => void;
  onRoomGone?: () => void;
  /** Scroll to this message once (global-search hit); parent clears it via
   *  onJumpConsumed so the same hit can be re-clicked later. */
  jumpToMessageId?: string | null;
  onJumpConsumed?: () => void;
}

function otherMember(
  members: ChatUserBrief[],
  currentUserId: string | undefined,
): ChatUserBrief | null {
  return members.find((m) => m.id !== currentUserId) ?? members[0] ?? null;
}

export function ChatRoomView({
  roomId,
  currentUserId,
  isDirector,
  isCeo,
  isArchivedView,
  showBack,
  onBack,
  onRoomGone,
  jumpToMessageId,
  onJumpConsumed,
}: ChatRoomViewProps) {
  const qc = useQueryClient();
  const room = useChatRoom(roomId);
  const messages = useChatMessagesInfinite(roomId);
  const send = useSendMessage(roomId);
  const editMessage = useEditMessage(roomId);
  const deleteMessage = useDeleteMessage(roomId);
  const toggleReaction = useToggleReaction(roomId);
  const clearReaction = useClearReaction(roomId);
  const markRead = useMarkRead(roomId);
  const pageAttended = usePageAttention();
  const archive = useArchiveRoom();
  const unarchive = useUnarchiveRoom();
  const setPinned = useSetRoomPinned();
  const updateRoom = useUpdateRoom();
  const uploadFile = useUploadFile();
  const [searchOpen, setSearchOpen] = useState(false);
  // Galeria „Multimedia i pliki" rozmowy (panel z prawej, jak na WhatsAppie).
  const [mediaOpen, setMediaOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [historyMessageId, setHistoryMessageId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  // Jump-to-old-message: when a search hit (or reply quote) points outside the
  // live buffer, the view switches to an "anchored" history query centered on
  // that message (?around=) instead of paging the whole gap down 50 at a time.
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [jumpTargetId, setJumpTargetId] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [reactionsModal, setReactionsModal] = useState<{
    message: ChatMessage;
    initialEmoji?: string;
  } | null>(null);
  // Map of user_id → { name, expiresAt }. Indicator shows entries whose
  // ``expiresAt > now``; we re-render on a 1s tick to age them out.
  const [typingByUser, setTypingByUser] = useState<
    Map<string, { name: string; expiresAt: number }>
  >(new Map());

  // Periodically prune expired typing entries from the map. Without this the
  // indicator could stay visible forever — filtering on read time alone
  // works only if the size never changes, but newly-arriving pings keep the
  // map non-empty even after the original sender stopped typing.
  useEffect(() => {
    if (typingByUser.size === 0) return;
    const i = setInterval(() => {
      setTypingByUser((prev) => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [id, info] of prev) {
          if (info.expiresAt <= now) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 700);
    return () => clearInterval(i);
  }, [typingByUser.size]);

  const pictureInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<MessageListHandle>(null);

  useChatRoomLive(roomId, {
    onTyping: (userId, userName) => {
      // Don't show "I am typing" to myself.
      if (currentUserId && userId === currentUserId) return;
      setTypingByUser((prev) => {
        const next = new Map(prev);
        next.set(userId, { name: userName, expiresAt: Date.now() + 3500 });
        return next;
      });
    },
  });

  // Reset typing state when switching rooms.
  useEffect(() => {
    setTypingByUser(new Map());
  }, [roomId]);

  // Mark this room as "open" so the global listener (useChatGlobalLive)
  // doesn't double-play the notification sound — this view handles its own.
  useEffect(() => {
    setActiveChatRoom(roomId);
    return () => setActiveChatRoom(null);
  }, [roomId]);

  const typingNames = useMemo(() => {
    const now = Date.now();
    const out: string[] = [];
    for (const [, info] of typingByUser) {
      if (info.expiresAt > now) out.push(info.name);
    }
    return out;
  }, [typingByUser]);

  const allMessages: ChatMessage[] = useMemo(() => {
    if (!messages.data) return [];
    // pages[0] = newest 50 (first fetch). fetchNextPage walks BACKWARDS in
    // time, so pages[1], pages[2]… contain progressively older history.
    // Reverse the pages so the flattened list is oldest → newest, which is
    // the order MessageList expects for its top-to-bottom rendering.
    return [...messages.data.pages].reverse().flatMap((p) => p.data);
  }, [messages.data]);

  const around = useChatMessagesAround(roomId, anchorId);
  const anchoredMessages: ChatMessage[] = useMemo(() => {
    if (!around.data) return [];
    // Same convention as the live buffer: fetchNextPage (older) appends,
    // fetchPreviousPage (newer) prepends — reversing yields oldest → newest.
    return [...around.data.pages].reverse().flatMap((p) => p.data);
  }, [around.data]);

  // Stay on the live buffer until the anchored page actually arrives, so the
  // list never flashes empty mid-jump.
  const anchoredActive = !!anchorId && anchoredMessages.length > 0;
  const displayedMessages = anchoredActive ? anchoredMessages : allMessages;

  // Mark-read fires once per NEW newest message, not on every render or every
  // change of the buffer length. Keying it on `allMessages.length` made a
  // refetch that merely re-shaped the buffer (a live-patched page holding 51
  // entries collapses back to the server's 50) look like new traffic and fire
  // a second POST, and each POST used to invalidate the whole room subtree.
  // Two open rooms were enough to exhaust the 300 req/min user throttle and
  // GET /chat/rooms/<id>/ started answering 429.
  const newestMessageId = allMessages.length
    ? allMessages[allMessages.length - 1].id
    : null;
  const lastMarkedRef = useRef<string | null>(null);

  useEffect(() => {
    lastMarkedRef.current = null;
  }, [roomId]);

  useEffect(() => {
    if (!roomId || isArchivedView) return;
    // Only mark read while the user can see the room. A background tab or a
    // minimised browser defers the call; it fires once attention returns
    // (the marker below stays unset, so the effect re-runs on focus).
    if (!pageAttended) return;
    // Don't spend a call on the empty buffer of a room whose first page is
    // still in flight — the effect re-runs once the messages land.
    if (!newestMessageId && messages.isLoading) return;
    // `null` = room really has no messages; still worth one call so the unread
    // badge clears, but only once per room.
    const marker = newestMessageId ?? "empty";
    if (lastMarkedRef.current === marker) return;
    lastMarkedRef.current = marker;
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, newestMessageId, isArchivedView, messages.isLoading, pageAttended]);

  // Dismiss push notification when the user focuses back on the window
  // while the room is already open (notification was shown because the
  // window was in the background).
  useEffect(() => {
    if (!roomId || isArchivedView) return;
    function onFocus() {
      import("../api/webPush").then(({ dismissRoomNotification }) =>
        dismissRoomNotification(roomId),
      );
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [roomId, isArchivedView]);

  function jumpToMessage(id: string) {
    if (listRef.current?.hasMessage(id)) {
      listRef.current.scrollToMessage(id);
    } else {
      // Not in the rendered buffer — anchor the history view on the target.
      // Works for arbitrarily old messages: one ?around= request instead of
      // paging the entire gap.
      setAnchorId(id);
      setJumpTargetId(id);
    }
  }

  // Scroll to the jump target once the anchored page has rendered.
  useEffect(() => {
    if (!jumpTargetId) return;
    if (listRef.current?.hasMessage(jumpTargetId)) {
      listRef.current.scrollToMessage(jumpTargetId);
      setJumpTargetId(null);
    }
  }, [jumpTargetId, displayedMessages]);

  // Leave the anchored history view and snap back to the live tail.
  function exitAnchor() {
    setAnchorId(null);
    setJumpTargetId(null);
  }

  useEffect(() => {
    // Runs after the live buffer is back on screen (and harmlessly on mount).
    if (!anchorId) listRef.current?.scrollToBottom();
  }, [anchorId]);

  // Reset the anchored view when switching rooms.
  useEffect(() => {
    setAnchorId(null);
    setJumpTargetId(null);
  }, [roomId]);

  // Jump requested from the global search panel — also covers the fresh-mount
  // case (room just opened from a hit): if the target isn't in the live
  // buffer, jumpToMessage anchors the history view on it.
  useEffect(() => {
    if (!jumpToMessageId) return;
    jumpToMessage(jumpToMessageId);
    onJumpConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToMessageId]);

  async function onPickGroupPicture(file: File) {
    try {
      const mf = await uploadFile.mutateAsync(file);
      await updateRoom.mutateAsync({
        roomId,
        pictureManagedFileId: mf.id,
      });
    } catch (err) {
      alert(uploadErrorMessage(err, "Nie udało się zaktualizować zdjęcia rozmowy."));
    }
  }

  if (room.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-500 bg-white">
        Ładowanie rozmowy…
      </div>
    );
  }

  if (!room.data) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-500 bg-white">
        Wybierz rozmowę z listy.
      </div>
    );
  }

  const isGroup = room.data.kind === "group";
  const other = otherMember(room.data.members ?? [], currentUserId);
  const title = isGroup
    ? room.data.name || "Pokój grupowy"
    : other?.full_name || other?.username || "Rozmowa";
  const subtitle = isGroup
    ? room.data.members.map((m) => m.full_name || m.username).join(", ")
    : other?.username || "";

  const groupPic = room.data.picture?.url ?? null;
  const privileged = isDirector || isCeo;
  // Group metadata (name + picture) can only be edited by directors and the
  // founder of the room — matches the backend permission gate.
  const canManageGroup =
    isGroup && (isDirector || room.data.created_by === currentUserId);

  return (
    <div className="flex-1 flex flex-col bg-white min-w-0 h-full">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-2 py-2 sm:px-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          {showBack && (
            <button
              type="button"
              onClick={onBack}
              title="Wróć"
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 -ml-1"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          {isGroup ? (
            canManageGroup ? (
              <button
                type="button"
                onClick={() => pictureInputRef.current?.click()}
                title="Kliknij aby zmienić zdjęcie grupy"
                className="relative w-9 h-9 rounded-full overflow-hidden flex-shrink-0 group"
              >
                {groupPic ? (
                  <img
                    src={groupPic}
                    alt={title}
                    className="w-9 h-9 object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                    <Users size={16} />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera size={14} className="text-white" />
                </div>
                <input
                  ref={pictureInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onPickGroupPicture(f);
                    e.target.value = "";
                  }}
                />
              </button>
            ) : groupPic ? (
              <img
                src={groupPic}
                alt={title}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                <Users size={16} />
              </div>
            )
          ) : (
            <Avatar user={other} size={36} />
          )}
          <div className="min-w-0">
            <h2 className="truncate text-[15px] font-bold leading-tight text-gray-900">
              {title}
              {room.data.is_archived && (
                <span className="ml-2 text-[10px] font-normal text-gray-500">
                  (zarchiwizowana)
                </span>
              )}
            </h2>
            {subtitle && (
              <p className="truncate font-mono text-[10px] uppercase tracking-wide text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isGroup && (
            <button
              type="button"
              onClick={() => setMembersOpen(true)}
              title="Członkowie rozmowy"
              className="p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600"
            >
              <UsersRound size={15} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setMediaOpen(true)}
            title="Multimedia i pliki"
            className="p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600"
          >
            <Images size={15} />
          </button>
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            title="Szukaj w rozmowie"
            className="p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600"
          >
            <Search size={15} />
          </button>
          {!isArchivedView && !room.data.is_archived && (
            <button
              type="button"
              disabled={setPinned.isPending}
              onClick={() =>
                setPinned.mutate({ roomId, pinned: !room.data.is_pinned })
              }
              title={
                room.data.is_pinned
                  ? "Odepnij rozmowę"
                  : "Przypnij rozmowę na górze listy"
              }
              aria-pressed={room.data.is_pinned}
              className={
                room.data.is_pinned
                  ? "p-1.5 text-amber-500 transition-colors hover:bg-gray-100 hover:text-amber-600 disabled:opacity-50"
                  : "p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
              }
            >
              <Pin size={15} />
            </button>
          )}
          {isDirector && !room.data.is_archived && (
            <button
              type="button"
              onClick={async () => {
                if (!confirm("Zarchiwizować rozmowę dla wszystkich?")) return;
                await archive.mutateAsync(roomId);
                onRoomGone?.();
              }}
              title="Archiwizuj"
              className="text-xs text-gray-600 hover:text-gray-900 hidden sm:flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
            >
              <Archive size={14} /> Archiwizuj
            </button>
          )}
          {isDirector && room.data.is_archived && (
            <button
              type="button"
              onClick={async () => {
                await unarchive.mutateAsync(roomId);
                onRoomGone?.();
              }}
              title="Cofnij archiwizację"
              className="text-xs text-gray-600 hover:text-gray-900 hidden sm:flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
            >
              <ArchiveRestore size={14} /> Przywróć
            </button>
          )}
        </div>
      </div>

      {mediaOpen && (
        <RoomMediaPanel
          roomId={roomId}
          roomTitle={title}
          onClose={() => setMediaOpen(false)}
          onJump={(messageId) => jumpToMessage(messageId)}
        />
      )}

      {searchOpen && (
        <SearchResultsPanel
          roomId={roomId}
          query={search}
          onQueryChange={setSearch}
          onClose={() => setSearchOpen(false)}
          onJump={(hit) => jumpToMessage(hit.id)}
        />
      )}

      <div className="flex-1 relative min-h-0 flex flex-col">
      <MessageList
        ref={listRef}
        messages={displayedMessages}
        currentUserId={currentUserId}
        privileged={privileged}
        highlightTerm={search.trim().length >= 2 ? search.trim() : ""}
        hasMore={anchoredActive ? !!around.hasNextPage : !!messages.hasNextPage}
        isFetchingMore={
          anchoredActive
            ? around.isFetchingNextPage
            : messages.isFetchingNextPage
        }
        onLoadMore={() =>
          anchoredActive ? around.fetchNextPage() : messages.fetchNextPage()
        }
        hasNewer={anchoredActive && !!around.hasPreviousPage}
        isFetchingNewer={anchoredActive && around.isFetchingPreviousPage}
        onLoadNewer={() => around.fetchPreviousPage()}
        pinToBottom={!anchoredActive}
        onReact={(messageId, emoji) =>
          toggleReaction.mutate(
            { messageId, emoji },
            {
              onSuccess: (updated) => {
                qc.setQueryData(
                  ["chat", "rooms", roomId, "messages"],
                  (
                    old:
                      | {
                          pages: { data: ChatMessage[]; meta: unknown }[];
                          pageParams: unknown[];
                        }
                      | undefined,
                  ) => {
                    if (!old) return old;
                    return {
                      ...old,
                      pages: old.pages.map((p) => ({
                        ...p,
                        data: p.data.map((m) =>
                          m.id === updated.id ? updated : m,
                        ),
                      })),
                    };
                  },
                );
              },
            },
          )
        }
        onClearReaction={(messageId) =>
          clearReaction.mutate(
            { messageId },
            {
              onSuccess: (updated) => {
                qc.setQueryData(
                  ["chat", "rooms", roomId, "messages"],
                  (
                    old:
                      | {
                          pages: { data: ChatMessage[]; meta: unknown }[];
                          pageParams: unknown[];
                        }
                      | undefined,
                  ) => {
                    if (!old) return old;
                    return {
                      ...old,
                      pages: old.pages.map((p) => ({
                        ...p,
                        data: p.data.map((m) =>
                          m.id === updated.id ? updated : m,
                        ),
                      })),
                    };
                  },
                );
              },
            },
          )
        }
        onReply={(m) => setReplyTo(m)}
        onEdit={(messageId, body) =>
          editMessage.mutate({ id: messageId, body })
        }
        onDelete={(messageId) => {
          if (!confirm("Usunąć tę wiadomość? Operacja jest logowana.")) return;
          deleteMessage.mutate(messageId);
        }}
        onShowHistory={(id) => setHistoryMessageId(id)}
        onJumpToReply={jumpToMessage}
        onOpenReactions={(message, emoji) =>
          setReactionsModal({ message, initialEmoji: emoji })
        }
        scrollKey={roomId}
      />
      {anchorId && !anchoredActive && around.isFetching && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-gray-900/80 text-white text-[11px] px-3 py-1.5 shadow-lg">
          Przenoszę do wiadomości…
        </div>
      )}
      {anchoredActive && (
        <button
          type="button"
          onClick={exitAnchor}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-gray-900/80 text-white text-[11px] px-3 py-1.5 shadow-lg hover:bg-gray-900"
        >
          <ChevronsDown size={13} /> Wróć do najnowszych
        </button>
      )}
      </div>

      {reactionsModal && (
        <ReactionsModal
          reactions={reactionsModal.message.reactions}
          members={room.data.members ?? []}
          currentUserId={currentUserId}
          initialEmoji={reactionsModal.initialEmoji}
          onClose={() => setReactionsModal(null)}
        />
      )}

      {membersOpen && isGroup && (
        <GroupMembersDialog
          room={room.data}
          canManage={canManageGroup}
          onClose={() => setMembersOpen(false)}
        />
      )}

      {historyMessageId && (
        <EditHistoryModal
          roomId={roomId}
          messageId={historyMessageId}
          onClose={() => setHistoryMessageId(null)}
        />
      )}

      {!isArchivedView && !room.data.is_archived ? (
        <>
          <TypingIndicator names={typingNames} />
          <MessageComposer
            roomId={roomId}
            replyTo={replyTo}
            onClearReply={() => setReplyTo(null)}
            onTyping={() => void postTypingPing(roomId)}
            onAfterSend={() => {
              // Sending from the anchored history view snaps back to the live
              // tail so the user sees their own message land.
              exitAnchor();
              listRef.current?.scrollToBottom();
            }}
            members={room.data.members ?? []}
            onSend={async (vars) => {
              await send.mutateAsync({
                body: vars.body,
                attachmentIds: vars.attachmentIds,
                replyToId: vars.replyToId,
                linkPreviews: vars.linkPreviews,
                mentionIds: vars.mentionIds,
              });
            }}
          />
        </>
      ) : (
        <div className="border-t border-gray-200 bg-[#f2f2f2] p-3 text-center text-xs text-gray-500">
          Rozmowa zarchiwizowana — wysyłanie nowych wiadomości wyłączone.
        </div>
      )}
    </div>
  );
}
